/** @jsxImportSource @emotion/react */

import { IconButton, Typography } from '@mui/material'
import { ChartData, Chart as ChartJs, Chart as ChartType, ChartOptions, registerables, TooltipModel } from 'chart.js'
import { Line } from 'react-chartjs-2'
import { useTheme } from '@emotion/react'

import { useCallback, useMemo, useState } from 'react'
import { throttle } from 'lodash'
import { findPromocodeHistoryData_findPromocodeHistoryGraph } from '__generatedTypes__/findPromocodeHistoryData'
import { PromocodeHistoryDisplayByTypeEnum } from '__generatedTypes__/globalTypes'
import { DateOption, DateOptionsEnum, Filters } from '../consts'
import { getDisplayBy } from '../utils'
import { ReactComponent as ArrowIcon } from './icons/arrow.svg'
import * as classes from './styles'
import { getDays, getMonths, mapDaysData, mapMonthData } from './utils'
import { ChartTooltip } from './ChartTooltip'

ChartJs.register(...registerables)

type Props = {
  data: findPromocodeHistoryData_findPromocodeHistoryGraph[]
  selectedDate: DateOption
  filters: Filters
  onNextClick: () => void
  onPrevClick: () => void
}

export const ChartStatistics = ({ data: dataProp, selectedDate, filters, onPrevClick, onNextClick }: Props) => {
  const theme = useTheme()

  const [tooltipData, setTooltipData] = useState<TooltipModel<'line'> | null>(null)

  const lifetimeSelected = selectedDate.id === DateOptionsEnum.Lifetime
  const currentYear = new Date().getFullYear()
  const year = filters.to.getFullYear()

  const displayBy = getDisplayBy(selectedDate, filters)
  const isByDays = displayBy === PromocodeHistoryDisplayByTypeEnum.DAYS
  const xAxis = useMemo(
    () => (isByDays ? getDays(filters) : getMonths(filters, selectedDate)),
    [isByDays, filters, selectedDate]
  )
  const data = useMemo(
    () => (isByDays ? mapDaysData(dataProp, xAxis) : mapMonthData(dataProp, xAxis, year)),
    [dataProp, isByDays, xAxis, year]
  )

  const customTooltip = useCallback(
    throttle((context: { chart: ChartType; tooltip: TooltipModel<'line'> }) => {
      setTooltipData({ ...context.tooltip } as TooltipModel<'line'>)
    }, 50),
    []
  )

  const options: ChartOptions<'line'> = {
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: customTooltip,
      },
    },
    scales: {
      y: {
        suggestedMin: 0,
        suggestedMax: 10,
        border: {
          dash: [7, 6],
          width: 0,
          dashOffset: 7,
        },
        ticks: {
          font: {
            size: 15,
            weight: 500,
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 15,
            weight: 500,
          },
        },
      },
    },
    interaction: {
      intersect: false,
    },
  }

  const chartData: ChartData<'line'> = {
    labels: xAxis,
    datasets: [
      {
        data: data?.map((item) => item?.promoUsed || 0),
        borderColor: theme.colors.red,
        backgroundColor: theme.colors.red,
        cubicInterpolationMode: 'monotone',
        pointRadius: 1,
        pointHoverRadius: 8,
        pointHoverBorderColor: theme.colors.white,
        pointHoverBorderWidth: 2,
      },
    ],
  }

  return (
    <div css={classes.wrapper}>
      <div css={[classes.row]}>
        <div css={classes.badge}>
          <span css={classes.dot}></span>
          <Typography variant="h6" css={classes.title}>
            Codes Used
          </Typography>
        </div>

        <div css={classes.actions}>
          <IconButton css={classes.button} onClick={onPrevClick} disabled={!lifetimeSelected}>
            <ArrowIcon />
          </IconButton>

          <IconButton css={classes.button} onClick={onNextClick} disabled={year === currentYear || !lifetimeSelected}>
            <ArrowIcon />
          </IconButton>
        </div>
      </div>

      <div css={classes.chartWrapper}>
        <Line data={chartData} options={options} />

        <ChartTooltip data={tooltipData} />
      </div>
    </div>
  )
}
